import { getStripeConnectSessionForUserPayments } from '@api/user';
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import { StyleBreakpoints } from '@constants';
import { Box, Tab, Tabs } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectAccountManagement,
  ConnectBalanceReport,
  ConnectBalances,
  ConnectComponentsProvider,
  ConnectNotificationBanner,
  ConnectPayments,
  ConnectPayouts,
  ConnectPayoutsList
} from '@stripe/react-connect-js';
import { shouldUseUSStripe } from '@utils/helpers';
import React, { useState } from 'react';
import { connect } from 'react-redux';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '12px',
    [theme.breakpoints.down(StyleBreakpoints.md)]: {
      margin: '0 auto'
    },
    [theme.breakpoints.up(StyleBreakpoints.lg)]: {
      maxWidth: '690px'
    },
    [theme.breakpoints.up(StyleBreakpoints.xl)]: {
      maxWidth: '1000px'
    }
  }
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box className="py-6 flex flex-col gap-6">
          {children}
        </Box>
      )}
    </div>
  );
}

const CompanyAccount = ({ user }) => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const [stripeConnectInstance] = useState(() => {
    const fetchClientSecret = async () => {
      try {
        const response = await getStripeConnectSessionForUserPayments();
        if (response && response.data && response.data.client_secret) {
          return response.data.client_secret;
        } else {
          console.error('Invalid response: ', response);
          return undefined;
        }
      } catch (error) {
        console.error('An error occurred: ', error.response?.data || error.message);
        return undefined;
      }
    };

    return loadConnectAndInitialize({
      publishableKey:
        user?.profile?.payments?.isPaymentsAccount === true
          ? (process.env.REACT_APP_STRIPE_PAYMENTS || process.env.REACT_APP_STRIPE_DEFAULT)
          : shouldUseUSStripe(user?.profile?.country)
            ? process.env.REACT_APP_STRIPE_US
            : process.env.REACT_APP_STRIPE_DEFAULT,
      fetchClientSecret: fetchClientSecret,
      appearance: {
        variables: {
          colorPrimary: '#6997FF',
          buttonPrimaryColorBackground: '#6997FF',
          buttonPrimaryColorBorder: '#6997FF',
          buttonPrimaryColorText: '#FFFFFF',
          actionPrimaryColorText: '#6997FF',
          actionPrimaryTextDecorationColor: '#6997FF',
          formHighlightColorBorder: '#6997FF',
          formAccentColor: '#6997FF',
          colorDanger: '#E926BF',
          badgeSuccessColorText: '#3CE5F4',
          badgeWarningColorText: '#D73EC6',
          badgeDangerColorText: '#D73EC6',
          spacingUnit: '12px',
          fontFamily: "'Montserrat',Helvetica,Arial,sans-serif"
        }
      }
    });
  });

  return (
    <>
      <TopNav />
      <div className={classes.container}>
        <TipDirectLeftMenu selectedTab={'Stripe'} />
        <div className={classes.content}>
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <ConnectNotificationBanner />
            <div className="bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 px-2 mt-4">
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Payments" />
                <Tab label="Payouts & Balance" />
                <Tab label="Reporting" />
                <Tab label="Account" />
              </Tabs>
            </div>

            <TabPanel value={tabValue} index={0}>
              <ConnectPayments />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <ConnectPayouts />
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <ConnectBalanceReport />
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              <ConnectAccountManagement />
            </TabPanel>
          </ConnectComponentsProvider>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  user: state.user.user
});

export default connect(mapStateToProps)(CompanyAccount);
